import { useEffect, useRef, useState } from 'react';

import type { AwsWafConfig } from '../types';
import { useAppDispatch } from '../stores/redux.store';
import { closeAwsCaptchaModal } from './aws-captcha-actions';
import { AWS_CAPTCHA_SCRIPT_ID } from './aws-captcha.constants';

type ScriptOutcome = 'not-started' | 'pending' | 'settled' | 'error';

type UseAwsWafCaptchaSdkScriptReturnType = {
  /** Whether or not the script has been loaded */
  hasLoaded: boolean;
  /** The script element that was appended to the document */
  scriptElement: HTMLScriptElement | null;
  /** Whether or not the script is in an error state */
  hasError: boolean;
  /** The 'state' of loading the script. */
  scriptOutcome: ScriptOutcome;
};

/**
 * Injects the AWS WAF Captcha SDK script once (or reuses an existing tag), listens for load/error,
 * and closes the captcha modal when the script fails or finishes without exposing `window.AwsWafCaptcha`.
 *
 * @returns `true` once the script tag has settled (existing tag, load, or sync SDK already on `window`).
 */
export const useAwsWafCaptchaSdkScript = (
  awsWafConfig: AwsWafConfig | null
): UseAwsWafCaptchaSdkScriptReturnType => {
  const dispatch = useAppDispatch();
  const scriptAppendedRef = useRef(false);
  const scriptOutcomeRef = useRef<ScriptOutcome>('not-started');
  const scriptElementRef = useRef<HTMLScriptElement | null>(null);
  const [scriptSettled, setScriptSettled] = useState(false);

  useEffect(() => {
    if (!awsWafConfig) {
      return;
    }

    // if the script element exists, then we have loaded the script already
    if (scriptAppendedRef.current) {
      scriptAppendedRef.current = true;
      scriptOutcomeRef.current = 'settled';
      setScriptSettled(true);

      // If the script element exists, but the global variable doesn't yet, we want to make sure the modal is closed to
      // prevent a blank screen from showing.
      // This situation should not happen (since the script loading should happen long before the user triggers any calls
      // to a protected endpoint
      if (!window.AwsWafCaptcha) {
        dispatch(closeAwsCaptchaModal());
      }
      return;
    }

    scriptAppendedRef.current = true;

    const scriptElement = document.createElement('script');
    scriptElement.id = AWS_CAPTCHA_SCRIPT_ID;
    scriptElement.src = awsWafConfig.integrationUrl;
    scriptElement.defer = true;

    scriptElementRef.current = scriptElement;

    scriptElement.addEventListener('error', () => {
      if (scriptOutcomeRef.current === 'pending') {
        dispatch(closeAwsCaptchaModal());
      }
    });

    scriptElement.addEventListener('load', () => {
      scriptOutcomeRef.current = 'settled';
      setScriptSettled(true);
      if (!window.AwsWafCaptcha) {
        dispatch(closeAwsCaptchaModal());
      }
    });

    document.head.appendChild(scriptElement);

    if (window.AwsWafCaptcha) {
      scriptOutcomeRef.current = 'settled';
      setScriptSettled(true);
    }

    return () => {
      // TODO: confirm whether or not we need to cleanup the script element - i.e. if it's loaded once, in what situation would we want to remove it?
      scriptElement.remove();
    };
  }, [dispatch, awsWafConfig]);

  return {
    hasLoaded: scriptSettled,
    scriptElement: scriptElementRef.current,
    scriptOutcome: scriptOutcomeRef.current,
    hasError: scriptOutcomeRef.current === 'error',
  };
};
